import { createAsyncThunk } from '@reduxjs/toolkit';

import { coursesApi, desktopsApi } from '@/Api';
import { selectCoursesPaginationSettings } from '@/Config/configSlice';
import type {
  IConnectToFreshStartDesktopRequest,
  IConnectToMyDesktopRequest,
  IConnectToParticipantDesktopRequest,
  IConnectToTrainerDesktopRequest,
  ICreateCourseRequest,
  ICreateFreshStartFromOsAsyncTemplateRequest,
  ICreateFreshStartFromStandaloneAsyncTemplateRequest,
  IDeleteDesktopAsyncRequest,
  IDeleteFreshStartDesktopRequest,
  IGetFreshStartServersForCourseRequest,
  IGetOsTemplatesOfServerRequest,
  ISaveIsFreshStartMachineReadyToCloneRequest,
  ISaveMachineIframeSizeRequest,
  ISaveParticipantsMachineControlActiveRequest,
  ISaveShowOnlyActiveMachinesRequest,
  ISaveWhetherTrainerAllowedPreviewRequest,
  IStartParticipantDesktopRequest,
  IStopDesktopRequest,
  IUpdateCourseRequest
} from '@/libs/models';
import { ResultHelpers } from '@/libs/models';

import type { GetCoursesParams } from './types';

export const getCourses = createAsyncThunk(
  'courses/getCourses',
  async (
    params: GetCoursesParams & { signal?: AbortSignal } = {},
    { getState, rejectWithValue }
  ) => {
    const state = getState() as any;
    const paginationSettings = selectCoursesPaginationSettings(state);

    const deduplicatedIncludeTags = params?.includeTags?.length
      ? [...new Set(params.includeTags)]
      : undefined;
    const deduplicatedExcludeTags = params?.excludeTags?.length
      ? [...new Set(params.excludeTags)]
      : undefined;

    const requestBody: any = {
      searchText: params?.searchText,
      includeTags: deduplicatedIncludeTags,
      excludeTags: deduplicatedExcludeTags,
      pageSize: params?.pageSize ?? paginationSettings.pageSize,
      page: params?.page
    };

    Object.keys(requestBody).forEach(key => {
      if (requestBody[key] === undefined) {
        delete requestBody[key];
      }
    });

    try {
      const result = await coursesApi.getCourses(requestBody, params.signal);
      const apiData = ResultHelpers.unwrap(result, 'Failed to get courses');

      return {
        ...apiData,
        isLoadingMore: params?.page && params.page > 1
      };
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ type: 'aborted' });
      }

      const message = error?.message ?? 'Failed to load courses';
      return rejectWithValue({ message });
    }
  }
);

export const getCourseTags = createAsyncThunk('courses/getCourseTags', async () => {
  try {
    const result = await coursesApi.getCourseTags();
    return ResultHelpers.unwrap(result, 'Failed to get course tags');
  } catch (error) {
    throw new Error(
      `Failed to fetch course tags: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
});

export const getCourse = createAsyncThunk('courses/getCourse', async (courseId: string) => {
  const result = await coursesApi.getCourse({ courseId });
  return ResultHelpers.unwrapWith(
    result,
    data => ({
      courseId,
      courseDetails: data
    }),
    'Failed to get course'
  );
});

export const getCourseJoinCode = createAsyncThunk(
  'courses/getCourseJoinCode',
  async (courseId: string) => {
    const result = await coursesApi.getCourseJoinCode({ courseId });
    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        joinCode: data.joinCode
      }),
      'Failed to get course join code'
    );
  }
);

export const getCourseForEdit = createAsyncThunk(
  'courses/getCourseForEdit',
  async (courseId: string) => {
    const result = await coursesApi.getCourseForEdit({ courseId });
    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        courseForEdit: data
      }),
      'Failed to get course for edit'
    );
  }
);

export const getMyDesktop = createAsyncThunk(
  'courses/getMyDesktop',
  async (params: { courseId: string; signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const result = await desktopsApi.getMyDesktop(params.courseId, params.signal);
      return ResultHelpers.unwrapWith(
        result,
        data => ({
          courseId: params.courseId,
          myDesktop: data
        }),
        'Failed to get my desktop'
      );
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ type: 'aborted', courseId: params.courseId });
      }

      throw error;
    }
  }
);

export const getTrainerDesktop = createAsyncThunk(
  'courses/getTrainerDesktop',
  async (params: { courseId: string; signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const result = await desktopsApi.getTrainerDesktop(
        { courseId: params.courseId },
        params.signal
      );
      return ResultHelpers.unwrapWith(
        result,
        data => ({
          courseId: params.courseId,
          trainerDesktop: data
        }),
        'Failed to get trainer desktop'
      );
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ type: 'aborted', courseId: params.courseId });
      }

      throw error;
    }
  }
);

export const getFreshStartDesktop = createAsyncThunk(
  'courses/getFreshStartDesktop',
  async (params: { courseId: string; signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const result = await desktopsApi.getFreshStartDesktop(params.courseId, params.signal);
      return ResultHelpers.unwrapWith(
        result,
        data => ({
          courseId: params.courseId,
          freshStartDesktop: data
        }),
        'Failed to get fresh start desktop'
      );
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ type: 'aborted', courseId: params.courseId });
      }

      throw error;
    }
  }
);

export const getCourseDesktops = createAsyncThunk(
  'courses/getCourseDesktops',
  async (params: { courseId: string; signal?: AbortSignal }, { rejectWithValue }) => {
    try {
      const result = await desktopsApi.getCourseDesktops(
        { courseId: params.courseId },
        params.signal
      );
      return ResultHelpers.unwrapWith(
        result,
        data => ({
          courseId: params.courseId,
          myDesktop: data?.myDesktop,
          trainerDesktop: data?.trainerDesktop
        }),
        'Failed to get course desktops'
      );
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return rejectWithValue({ type: 'aborted', courseId: params.courseId });
      }

      throw error;
    }
  }
);

export const getCoursesMyDesktops = createAsyncThunk(
  'courses/getCoursesMyDesktops',
  async (courseIds: string[]) => {
    const result = await desktopsApi.getMyDesktopsForCourses({ courseIds });
    return ResultHelpers.unwrap(result, 'Failed to get my desktops for courses');
  }
);

export const getCoursesTrainerDesktops = createAsyncThunk(
  'courses/getCoursesTrainerDesktops',
  async (courseIds: string[]) => {
    const result = await desktopsApi.getTrainerDesktopsForCourses({ courseIds });
    return ResultHelpers.unwrap(result, 'Failed to get trainer desktops for courses');
  }
);

export const getParticipants = createAsyncThunk(
  'courses/getParticipants',
  async (courseId: string) => {
    const result = await desktopsApi.getParticipants({ courseId });
    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        participants: data.participants
      }),
      'Failed to get participants'
    );
  }
);

export const saveIsFreshStartMachineReadyToClone = createAsyncThunk(
  'courses/saveIsFreshStartMachineReadyToClone',
  async (request: ISaveIsFreshStartMachineReadyToCloneRequest) => {
    await coursesApi.saveIsFreshStartMachineReadyToClone(request);
    return request;
  }
);

export const saveWhetherTrainerAllowedPreview = createAsyncThunk(
  'courses/saveWhetherTrainerAllowedPreview',
  async (request: ISaveWhetherTrainerAllowedPreviewRequest) => {
    await coursesApi.saveWhetherTrainerAllowedPreview(request);
    return request;
  }
);

export const saveShowOnlyActiveMachines = createAsyncThunk(
  'courses/saveShowOnlyActiveMachines',
  async (request: ISaveShowOnlyActiveMachinesRequest) => {
    await coursesApi.saveShowOnlyActiveMachines(request);
    return request;
  }
);

export const saveParticipantsMachineControlActive = createAsyncThunk(
  'courses/saveParticipantsMachineControlActive',
  async (request: ISaveParticipantsMachineControlActiveRequest) => {
    await coursesApi.saveParticipantsMachineControlActive(request);
    return request;
  }
);

export const saveMachineIframeSize = createAsyncThunk(
  'courses/saveMachineIframeSize',
  async (request: ISaveMachineIframeSizeRequest) => {
    await coursesApi.saveMachineIframeSize(request);
    return request;
  }
);

export const createFreshStartFromOsTemplate = createAsyncThunk(
  'courses/createFreshStartFromOsTemplate',
  async (request: ICreateFreshStartFromOsAsyncTemplateRequest) => {
    await desktopsApi.createFreshStartFromOsTemplate(request);
    return request;
  }
);

export const createFreshStartFromStandaloneTemplate = createAsyncThunk(
  'courses/createFreshStartFromStandaloneTemplate',
  async (request: ICreateFreshStartFromStandaloneAsyncTemplateRequest) => {
    await desktopsApi.createFreshStartFromStandaloneTemplate(request);
    return request;
  }
);

export const deleteFreshStartDesktop = createAsyncThunk(
  'courses/deleteFreshStartDesktop',
  async (request: IDeleteFreshStartDesktopRequest) => {
    await desktopsApi.deleteFreshStartDesktop(request);
    return request;
  }
);

export const deleteMyDesktop = createAsyncThunk(
  'courses/deleteMyDesktop',
  async (request: IDeleteDesktopAsyncRequest) => {
    await desktopsApi.deleteDesktop(request);
    return {
      courseId: request.courseId
    };
  }
);

export const deleteParticipantDesktop = createAsyncThunk(
  'courses/deleteParticipantDesktop',
  async ({
    courseId,
    desktopInstanceId,
    participantId
  }: {
    courseId: string;
    desktopInstanceId: number;
    participantId: string;
  }) => {
    await desktopsApi.deleteDesktop({
      courseId,
      instanceId: desktopInstanceId
    } as IDeleteDesktopAsyncRequest);

    return {
      courseId,
      participantId
    };
  }
);

export const startParticipantDesktop = createAsyncThunk(
  'courses/startParticipantDesktop',
  async ({
    courseId,
    participantId
  }: {
    courseId: string;
    desktopInstanceId: number;
    participantId: string;
  }) => {
    const result = await desktopsApi.startParticipantDesktop({
      courseId,
      participantId
    } as IStartParticipantDesktopRequest);

    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        participantId,
        participantDesktop: data
      }),
      'Failed to start participant desktop'
    );
  }
);

export const stopParticipantDesktop = createAsyncThunk(
  'courses/stopParticipantDesktop',
  async ({
    courseId,
    desktopInstanceId,
    participantId
  }: {
    courseId: string;
    desktopInstanceId: number;
    participantId: string;
  }) => {
    await desktopsApi.stopDesktop({
      courseId,
      instanceId: desktopInstanceId
    } as IStopDesktopRequest);

    return {
      courseId,
      participantId
    };
  }
);

export const stopMyDesktop = createAsyncThunk(
  'courses/stopMyDesktop',
  async ({ courseId, desktopInstanceId }: { courseId: string; desktopInstanceId: number }) => {
    await desktopsApi.stopDesktop({
      courseId,
      instanceId: desktopInstanceId
    } as IStopDesktopRequest);
    return { courseId };
  }
);

export const stopTrainerDesktop = createAsyncThunk(
  'courses/stopTrainerDesktop',
  async ({ courseId, desktopInstanceId }: { courseId: string; desktopInstanceId: number }) => {
    await desktopsApi.stopDesktop({
      courseId,
      instanceId: desktopInstanceId
    } as IStopDesktopRequest);
    return { courseId };
  }
);

export const stopFreshStartDesktop = createAsyncThunk(
  'courses/stopFreshStartDesktop',
  async ({ courseId, desktopInstanceId }: { courseId: string; desktopInstanceId: number }) => {
    await desktopsApi.stopDesktop({
      courseId,
      instanceId: desktopInstanceId
    } as IStopDesktopRequest);
    return { courseId };
  }
);

export const connectToMyDesktop = createAsyncThunk(
  'courses/connectToMyDesktop',
  async (courseId: string) => {
    const result = await desktopsApi.connectToMyDesktop({
      courseId
    } as IConnectToMyDesktopRequest);

    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        myDesktop: data
      }),
      'Failed to connect to my desktop'
    );
  }
);

export const connectToFreshStartDesktop = createAsyncThunk(
  'courses/connectToFreshStartDesktop',
  async (courseId: string) => {
    const result = await desktopsApi.connectToFreshStartDesktop({
      courseId
    } as IConnectToFreshStartDesktopRequest);

    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        freshStartDesktop: data
      }),
      'Failed to connect to fresh start desktop'
    );
  }
);

export const connectToTrainerDesktop = createAsyncThunk(
  'courses/connectToTrainerDesktop',
  async (courseId: string) => {
    const result = await desktopsApi.connectToTrainerDesktop({
      courseId
    } as IConnectToTrainerDesktopRequest);

    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId,
        trainerDesktop: data
      }),
      'Failed to connect to trainer desktop'
    );
  }
);

export const connectToParticipantDesktop = createAsyncThunk(
  'courses/connectToParticipantDesktop',
  async (request: IConnectToParticipantDesktopRequest) => {
    const result = await desktopsApi.connectToParticipantDesktop(request);
    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId: request.courseId,
        participantId: request.participantId,
        participantDesktop: data
      }),
      'Failed to connect to participant desktop'
    );
  }
);

export const getStandalones = createAsyncThunk('courses/getStandalones', async () => {
  const result = await desktopsApi.getStandalones();
  return ResultHelpers.unwrap(result, 'Failed to get standalones');
});

export const getFreshStartServersForCourse = createAsyncThunk(
  'courses/getFreshStartServersForCourse',
  async (request: IGetFreshStartServersForCourseRequest) => {
    const result = await coursesApi.getFreshStartServersForCourse(request);
    return ResultHelpers.unwrapWith(
      result,
      data => ({
        courseId: request.courseId,
        servers: data
      }),
      'Failed to get fresh start servers'
    );
  }
);

export const getOsTemplatesOfServer = createAsyncThunk(
  'courses/getOsTemplatesOfServer',
  async (request: IGetOsTemplatesOfServerRequest) => {
    const result = await coursesApi.getOsTemplatesOfServer(request);

    const unwrapped = ResultHelpers.unwrapWith(
      result,
      data => ({
        serverName: request.serverName,
        templates: data.osTemplateNames
      }),
      'Failed to get OS templates'
    );

    return unwrapped;
  }
);

export const createCourse = createAsyncThunk(
  'courseManagement/createCourse',
  async (request: ICreateCourseRequest) => {
    const response = await coursesApi.createCourse(request);
    return response.data;
  }
);

export const updateCourse = createAsyncThunk(
  'courseManagement/updateCourse',
  async (request: IUpdateCourseRequest) => {
    await coursesApi.updateCourse(request);
    return null;
  }
);

export { clearCourseForEditError, clearCreateCourseResult, clearUpdateCourseResult } from './slice';
